<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { EQUIPMENT_PAGE_SIZES } from '@/composables/equipment/useEquipmentList';
import EquipmentSelect from './EquipmentSelect.vue';
import './equipment-ui.css';

defineProps<{ pageCount: number; total: number }>();
const page = defineModel<number>('page', { required: true });
const pageSize = defineModel<number>('pageSize', { required: true });

const { t } = useI18n({ useScope: 'global' });
const sizes = computed(() =>
  EQUIPMENT_PAGE_SIZES.map((size) => ({
    value: size as number,
    label: t('equipment.perPage', { n: size }),
  })),
);
</script>

<template>
  <footer class="eqp">
    <span class="eq-muted">{{ t('equipment.total', { n: total }) }}</span>
    <nav class="eqp__nav" :aria-label="t('equipment.pagination')">
      <button
        type="button"
        class="eq-btn eq-btn--icon"
        :disabled="page <= 1"
        :aria-label="t('equipment.previous')"
        @click="page--"
      >
        <i class="pi pi-chevron-left" />
      </button>
      <span class="eqp__page">{{ page }} / {{ pageCount }}</span>
      <button
        type="button"
        class="eq-btn eq-btn--icon"
        :disabled="page >= pageCount"
        :aria-label="t('equipment.next')"
        @click="page++"
      >
        <i class="pi pi-chevron-right" />
      </button>
    </nav>
    <EquipmentSelect
      v-model="pageSize"
      class="eqp__size"
      :options="sizes"
      :search-above="0"
      :aria-label="t('equipment.pageSize')"
    />
  </footer>
</template>

<style scoped>
.eqp {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--nxt-outline-variant);
  font-size: 0.8125rem;
}
.eqp__nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.eqp__page {
  min-width: 3.5rem;
  text-align: center;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.eqp__size {
  width: 9.5rem;
}
</style>
